/** @copyright (c) Viewpost. All Rights Reserved. See LICENSE for more details. */
/* eslint max-len:0 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { isFunction } from 'lodash';
import t from 'tcomb-validation';
import useApi from 'api/hooks/useApi';

import logonEndpoint, { ssoCheck } from 'api/session/logon';

import Button from 'components/Button';
import PasswordToggleTextBox from 'components/Form/Inputs/PasswordToggleTextBox';
import FormJSX from 'components/Form/FormJSX';
import FieldOption from 'components/Form/FieldOption';
import PathLink from 'components/Link/PathLink';
import { SmallBannerNotification, NotificationStyleTypes } from 'components/Notification';

import FloatingAlert from 'containers/Logon/FloatingAlert';

import { NetworkErrors } from 'config/constants';
import LogonFailureReason, {
  CredentialsFailureReason
} from 'config/constants/session/LogonFailureReason';

import createExecuteApiResultHooks from 'hooks/apiResult/createExecuteApiResultHooks';
import useIsMobile from 'hooks/browser/useIsMobile';
import useQueryParams from 'hooks/location/useQueryParams';

import { getUtcOffset, getTimeZone } from 'utils/dateUtils';
import { isValidEmail } from 'schemas/common/email/constants';

import Messages from './index.messages';
import './index.scss';

const LogonHooks = createExecuteApiResultHooks({
  endpoint: logonEndpoint
});

function EmailForm({
  email,
  onChangeEmail,
  isLoading
}) {
  const isMobile = useIsMobile();
  const formRef = useRef();
  const [formState, setFormState] = useState({
    userName: email
  });

  const onSubmit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    let value = formRef?.current?.getValue();
    if (value) {
      onChangeEmail?.(value.userName);
    }
  };

  return (
    <form onSubmit={onSubmit}>
      <FormJSX
        ref={formRef}
        modelType={t.struct({
          userName: t.String
        })}
        value={formState}
        onChange={v => setFormState(v)}
      >
        <FieldOption
          name="userName"
          label="Email Address"
          autoFocus={true}
          tabIndex={1}
        />
      </FormJSX>
      <Button
        type="submit"
        expand={true}
        isDisabled={isLoading}
        isProcessing={isLoading}
        tall={isMobile}
        bold={isMobile}
        sharpCorners={isMobile}
        tabIndex="2"
      >
        Continue
      </Button>
    </form>
  );
}

function SsoForm({
  email,
  providerName,
  url,
  onChangeEmail
}) {
  const isMobile = useIsMobile();
  const formRef = useRef();
  const [formState, setFormState] = useState({
    email
  });

  const onSubmit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    window.location.href = url;
  };

  return (
    <form onSubmit={onSubmit}>
      <FormJSX
        ref={formRef}
        modelType={t.struct({
          email: t.String
        })}
        value={formState}
        onChange={v => setFormState(v)}
      >
        <FieldOption
          name="email"
          label="Email Address"
          disabled={true}
          rightLabel={<a onClick={() => onChangeEmail?.()} style={{ fontSize: '12px'}}>
            Change
          </a>}
        />
      </FormJSX>
      <Button
        type="submit"
        expand={true}
        tall={isMobile}
        bold={isMobile}
        sharpCorners={isMobile}
        tabIndex="2"
      >
        Log In with {providerName}
      </Button>
    </form>
  );
}

const LogonForm = ({
  email,
  onChangeEmail,

  onUpdate,

  credentialsFailure,
  failure,

  // api pass thru
  changeEmailToken,
  ignoreWipe,
  invitationId,

  // function or 'link' (default)
  resetPasswordMethod
}) => {
  // Hooks
  const isMobile = useIsMobile();
  const formRef = useRef();

  const { destinationCompanyId } = useQueryParams(['destinationCompanyId']);

  const executeLogon = LogonHooks.useExecuteRequest();
  const isLoading = LogonHooks.useIsLoading();
  const error = LogonHooks.useRequestError();

  const [ formState, setFormState ] = useState({
    userName: email
  });

  const onSubmit = useMemo(
    () => async (event) => {
      event.preventDefault();
      event.stopPropagation();

      if (formRef.current) {
        const value = formRef.current.getValue();

        if (value) {
          let deviceToken = null;

          try {
            deviceToken = localStorage.getItem(`DeviceId-${value.userName}`);
          } catch {
            // Local storage no-worky, nothing we can do
          }

          const [, action] = await executeLogon(
            {
              userName: value.userName,
              password: value.password,
              deviceToken,
              ignoreWipe,
              changeEmailToken,
              invitationId,
              companyId: failure !== LogonFailureReason.DoesNotBelongToCompany
                ? destinationCompanyId
                : null,
              clientInfo: {
                localDeviceTimeZone: getTimeZone(),
                localDeviceUtcOffset: getUtcOffset()
              }
            },
            {
              failureMessage: false
            }
          );

          onUpdate(action.payload.result);
        }
      }
    },
    []
  );

  const resetPasswordLink = useMemo(
    () => {
      if (isFunction(resetPasswordMethod)) {
        return (
          <Button
            onClick={resetPasswordMethod}
            className="forgot-password"
            anchor={true}
            tabIndex={4}
          >
            <Messages.ResetPassword.Message />
          </Button>
        );
      }

      if (resetPasswordMethod == null || resetPasswordMethod === 'link') {
        return (
          <PathLink.Logon.ResetPassword
            className="forgot-password"
            tabIndex={4}
          >
            <Messages.ResetPassword.Message />
          </PathLink.Logon.ResetPassword>
        );
      }
    },
    [ resetPasswordMethod ]
  );

  // Render
  const isRateLimited = error === NetworkErrors.TooManyRequests;

  let alert = null;

  if (!isLoading) {
    if (isRateLimited) {
      alert = (
        <SmallBannerNotification type={NotificationStyleTypes.Error} shakeOnEnter={true}>
          <Messages.TooManyRequests.Message />
        </SmallBannerNotification>
      );
    } else if (failure === LogonFailureReason.InvalidCredentials
      && credentialsFailure === CredentialsFailureReason.InvalidUserName) {
      alert = (
        <SmallBannerNotification type={NotificationStyleTypes.Warning} shakeOnEnter={true}>
          <Messages.InvalidUserName.Message />
        </SmallBannerNotification>
      );
    } else if (failure === LogonFailureReason.InvalidCredentials
      && credentialsFailure === CredentialsFailureReason.InvalidPassword) {
      alert = (
        <SmallBannerNotification type={NotificationStyleTypes.Warning} shakeOnEnter={true}>
          <Messages.InvalidPassword.Message />
        </SmallBannerNotification>
      );
    } else if (failure === LogonFailureReason.DoesNotBelongToCompany) {
      alert = (
        <SmallBannerNotification type={NotificationStyleTypes.Warning} shakeOnEnter={true}>
          <Messages.DoesNotBelongToCompany.Message />
        </SmallBannerNotification>
      );
    }

    if (alert) alert = <FloatingAlert>{alert}</FloatingAlert>;
  }

  return (
    <form onSubmit={onSubmit}>
      {alert}
      <FormJSX
        ref={formRef}
        modelType={t.struct({
          userName: t.String,
          password: t.String
        })}
        value={formState}
        onChange={v => setFormState(v)}
      >
        <FieldOption
          name="userName"
          label="Email Address"
          disabled={true}
          rightLabel={<a onClick={() => onChangeEmail?.()} style={{ fontSize: '12px' }}>
            Change
          </a>}
        />
        <FieldOption
          name="password"
          label="Password"
          rightLabel={resetPasswordLink}
          autoFocus={true}
          inputComponent={PasswordToggleTextBox} />
      </FormJSX>
      <Button
        analyticsId="LoginButton"
        type="submit"
        isDisabled={isLoading}
        isProcessing={isLoading}
        expand={true}
        tall={isMobile}
        bold={isMobile}
        sharpCorners={isMobile}
        tabIndex="2"
      >
        Log In
      </Button>
    </form>
  );
};

function OrBlock() {
  return <div className="subtext" style={{
    display: 'flex',
    gap: '10px',
    margin: '20px 0px'
  }}>
    <div style={{
      flex: 1,
      background: 'linear-gradient(to bottom, transparent calc(50% - 0.5px), #ddd calc(50% - 0.5px), #ddd calc(50% + 0.5px), transparent calc(50% + 0.5px))'
    }}>&nbsp;</div>
    <div>
      OR
    </div>
    <div style={{
      flex: 1,
      background: 'linear-gradient(to bottom, transparent calc(50% - 0.5px), #ddd calc(50% - 0.5px), #ddd calc(50% + 0.5px), transparent calc(50% + 0.5px))'
    }}>&nbsp;</div>
  </div>;
}

function CredentialsForm({
  redirect,
  ...logonProps
}) {
  const dispatch = useDispatch();
  const isMobile = useIsMobile();

  const { userName } = useQueryParams(['userName']);

  const [email, setEmail] = useState(userName);
  const [isEmailEntered, setEmailEntered] = useState(isValidEmail(userName));
  const [isSsoToggled, toggleSso] = useState(true);

  const ssoCheckApi = useApi(ssoCheck, 'SsoCheck');

  useEffect(
    () => {
      if (redirect) {
        dispatch({ type: 'SaveLogonRedirect', redirect });
      }
    },
    [redirect]
  );

  useEffect(() => {
    if (email) {
      ssoCheckApi.call({
        'login_hint': email,
        'target_link_uri': redirect ?? window.location.pathname
      });
    }
  }, [email, redirect]);

  // Step 1: Enter Email
  if (!isEmailEntered || ssoCheckApi.isLoading()) {
    return <EmailForm
      isLoading={ssoCheckApi.isLoading()}
      email={email}
      onChangeEmail={(e) => {
        setEmail(e);
        setEmailEntered(true);
      }} />;
  }

  // Determine SSO based on email
  const isSsoSupported = ssoCheckApi.getResponse()?.enabled;
  const isSsoEnforced = ssoCheckApi.getResponse()?.enforced;
  const isSsoLogin = isSsoSupported && (isSsoEnforced || isSsoToggled);
  const providerName = ssoCheckApi.getResponse()?.name;

  let toggleSsoOption = null;
  if (isSsoSupported && !isSsoEnforced) {
    toggleSsoOption = <div style={{ textAlign: 'center' }}>
      <OrBlock />
      <a onClick={() => toggleSso(!isSsoToggled)}>
        Switch to Log In with {isSsoToggled ? 'Viewpost' : providerName}
      </a>
    </div>;
  }

  return (
    <div style={{

      // special case for landing pages where login is shown on mobile in a sliding panel
      padding: isMobile && logonProps?.variant === 'small'
        ? '12px 40px calc(32px - 1rem) 40px'
        : null
    }}>
      {isSsoLogin
        ? <SsoForm email={email}
          onChangeEmail={() => setEmailEntered(false)}
          providerName={providerName}
          url={ssoCheckApi.getResponse()?.signInUrl} />
        : <LogonForm email={email}
          onChangeEmail={() => setEmailEntered(false)}
          {...logonProps }/>}
      {toggleSsoOption}
    </div>
  );
}

export default CredentialsForm;
